import { formatWorldClockPrompt } from '../../ui/worldRuntime/clock.js';
import {
  formatActiveDevices,
  formatActor,
  formatAnswers,
  formatCompactSummaries,
  formatEvents,
  formatLocation,
  formatPeriod,
  formatSchedule,
} from '../format.js';
import type { PromptSourceBundle } from '../types.js';
import {
  buildWorldContext,
  filterActors,
  filterEventsForActors,
} from '../filters.js';
import type { CompileSections } from './base.js';

/** Full world-generation path — Tier 0–1, seed, taxonomy, exclusions. No actors/history. */
export interface WorldGenerationContextInput {
  source: PromptSourceBundle;
  taxonomyBlock: string;
  exclusionBlock: string;
}

export function buildWorldGenerationSections(input: WorldGenerationContextInput): CompileSections {
  const world = buildWorldContext(input.source);
  const seed = input.source.generationSeed ?? `${input.source.worldId}-devices`;

  const tier0 = [
    `LOCATION: ${formatLocation(world)}`,
    `PERIOD: ${formatPeriod(world.timePeriod)}`,
    `ACCEPTED_ANSWERS:\n${formatAnswers(world.answers)}`,
    `GENERATION_SEED: ${seed}`,
  ].join('\n\n');

  const tier1 = [
    `SCHEDULE: ${formatSchedule(world.schedule)}`,
    `OPERATING_STATUS: ${input.source.operatingStatus}`,
    `EPISODE: ${input.source.clock.episodeNumber}`,
    `CONFIRMED_SNAPSHOT: v${input.source.worldVersion}`,
    input.taxonomyBlock,
    input.exclusionBlock,
  ].join('\n\n');

  return { tier0, tier1 };
}

/** Compact routine-dialogue path — Tier 0–3 for selected actors only, bounded history. */
export interface CompactDialogueContextInput {
  source: PromptSourceBundle;
  actorIds: string[];
  maxRecentEvents?: number;
  maxSummaries?: number;
}

const DEFAULT_MAX_EVENTS = 8;
const DEFAULT_MAX_SUMMARIES = 2;

export function buildCompactDialogueSections(input: CompactDialogueContextInput): {
  sections: CompileSections;
  eventIds: string[];
  summaryIds: string[];
  actorIds: string[];
} {
  const world = buildWorldContext(input.source);
  const actorIds = [...new Set(input.actorIds)];
  const actors = filterActors(input.source.allActors, actorIds);
  const maxEvents = input.maxRecentEvents ?? DEFAULT_MAX_EVENTS;
  const maxSummaries = input.maxSummaries ?? DEFAULT_MAX_SUMMARIES;

  const events = filterEventsForActors(input.source.events, actorIds, maxEvents);
  const eventIds = events.map((e) => e.eventId);
  const compactSummaries = input.source.summaries.slice(-maxSummaries);
  const summaryIds = compactSummaries.map((s) => s.summaryId);

  const tier0 = [
    `LOCATION: ${formatLocation(world)}`,
    `PERIOD: ${formatPeriod(world.timePeriod)}`,
  ].join('\n');

  const clock = formatWorldClockPrompt(
    input.source.clock.inWorldIso,
    world.schedule,
    world.location.region,
  );
  const tier1 = [
    `CLOCK: ${clock}. This is the only year, date, and time.`,
    `SCHEDULE: ${formatSchedule(world.schedule)}`,
    `OPERATING_STATUS: ${input.source.operatingStatus}`,
    `ACTIVE_DEVICES:\n${formatActiveDevices(input.source.activeDevices)}`,
  ].join('\n');

  const tier2 = actors.length
    ? `ACTORS:\n${actors.map(formatActor).join('\n')}`
    : undefined;

  const historyParts = [
    compactSummaries.length
      ? `COMPACT_SUMMARIES:\n${formatCompactSummaries(compactSummaries)}`
      : '',
    events.length ? `RECENT_EVENTS:\n${formatEvents(events)}` : '',
  ].filter(Boolean);
  const tier3 = historyParts.length ? historyParts.join('\n\n') : undefined;

  return {
    sections: { tier0, tier1, tier2, tier3 },
    eventIds,
    summaryIds,
    actorIds,
  };
}
